import { useState } from 'react';
import useFindStoresQuery from '@/api/queries/useFindStoresQuery';
import Alert from '@/components/Alert';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import StoreCard from '@/modules/retailer/components/StoreCard';
import { Store } from '@/modules/retailer/models/Store';
import { HomePath } from '@/routing/paths';
import { useNavigate } from 'react-router-dom';

const Stores = () => {
  const navigate = useNavigate();
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const { switchContext } = useAuth();

  const page = 1;
  const { stores, isLoading, error } = useFindStoresQuery(page, '', undefined, 100);

  const enterStore = (store: Store) => {
    void (async () => {
      try {
        await switchContext({
          organizationId: store.organizationId,
          storeId: store.id,
        });
        navigate(HomePath);
      } catch (err) {
        console.error('Failed to switch context:', err);
      }
    })();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert type="error" message="Failed to load stores." />
      </div>
    );
  }

  const hasStores = (stores?.items.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Stores" />

      {!hasStores ? (
        <div className="bg-white shadow-sm rounded-2xl p-6">
          <Alert type="info" message="No stores found for your account." />
        </div>
      ) : (
        <section className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-700">Choose a store</h3>
            <p className="text-sm text-gray-600 mt-1">
              Select a store to open its ordering workspace.
            </p>
          </div>

          {selectedStore?.status && selectedStore.status !== 'ACTIVE' && (
            <Alert
              type="warning"
              message="This store is not active. Some actions may be disabled."
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {stores.items.map((store) => (
              <StoreCard
                key={store.id}
                store={store}
                selected={selectedStore?.id === store.id}
                onSelect={() => {
                  setSelectedStore(store);
                  enterStore(store);
                }}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Stores;
